"use client"

import * as React from "react"
import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

// ─── Config ──────────────────────────────────────────────
const TOAST_LIMIT = 3
const TOAST_REMOVE_DELAY = 4000

// ─── Types ──────────────────────────────────────────────
export type ToastVariant = "default" | "destructive" | "success" | "warning" | "info"

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  variant?: ToastVariant
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST", 
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes
type Action =
  | { type: ActionType["ADD_TOAST"]; toast: ToasterToast }
  | { type: ActionType["UPDATE_TOAST"]; toast: Partial<ToasterToast> }
  | { type: ActionType["DISMISS_TOAST"]; toastId?: ToasterToast["id"] }
  | { type: ActionType["REMOVE_TOAST"]; toastId?: ToasterToast["id"] }

interface State {
  toasts: ToasterToast[]
}

// ─── Helpers ──────────────────────────────────────────────
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({ type: "REMOVE_TOAST", toastId })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

// ─── Reducer ──────────────────────────────────────────────
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }
    case "DISMISS_TOAST": {
      const { toastId } = action
      if (toastId) addToRemoveQueue(toastId)
      else state.toasts.forEach((t) => addToRemoveQueue(t.id))
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return { ...state, toasts: [] }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

// ─── State Management ─────────────────────────────────────
const listeners: Array<(state: State) => void> = []
let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => listener(memoryState))
}

// ─── Toast Functions ──────────────────────────────────────
type ToastInput = Omit<ToasterToast, "id">

function toast({ variant = "default", ...props }: ToastInput) {
  const id = genId()

  const update = (props: Partial<ToasterToast>) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      variant,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return { id, dismiss, update }
}

// ─── Variant Helper Functions ─────────────────────────────
const createToastVariant = (variant: ToastVariant) => {
  return (props: Omit<ToastInput, "variant">) => toast({ ...props, variant })
}

// Export specific toast functions for each variant
toast.success = createToastVariant("success")
toast.error = createToastVariant("destructive")  
toast.warning = createToastVariant("warning")
toast.info = createToastVariant("info")
toast.default = createToastVariant("default")

// Quick toast functions with common patterns
toast.promise = <T,>(
  promise: Promise<T>,
  {
    loading,
    success, 
    error,
  }: {
    loading: ToastInput
    success: ToastInput | ((data: T) => ToastInput)
    error: ToastInput | ((error: any) => ToastInput)
  }
) => {
  const toastId = toast({ ...loading, variant: "info" }).id

  promise
    .then((data) => {
      const successToast = typeof success === "function" ? success(data) : success
      toast.success(successToast)
    })
    .catch((err) => {
      const errorToast = typeof error === "function" ? error(err) : error
      toast.error(errorToast)
    })
    .finally(() => {
      dispatch({ type: "DISMISS_TOAST", toastId })
    })

  return promise
}

// ─── Hook ────────────────────────────────────────────────
function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) listeners.splice(index, 1)
    }
  }, [])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }